import { logger } from "@/lib/logger"
import { isClientNetworkError } from "@/lib/networkError"

import {
  AWS_MARKETPLACE_ONBOARDING_ERROR_CODES,
  BILLING_API_BASE_URL,
} from "./constants"
import type {
  BillingErrorBody,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  MarketplaceOnboardingRequest,
  MarketplaceOnboardingResponse,
  PortalFlow,
  PortalSessionResponse,
  SignupInitRequest,
  SignupInitResponse,
  SubscriptionResponse,
  UsageResponse,
} from "./types"

export class BillingApiError extends Error {
  readonly status: number
  readonly errorCode: string
  readonly body?: BillingErrorBody

  constructor(
    status: number,
    body: BillingErrorBody | undefined,
    fallback: string,
  ) {
    super(body?.message ?? fallback)
    this.name = "BillingApiError"
    this.status = status
    this.errorCode = body?.error_code ?? "UNKNOWN"
    this.body = body
  }
}

const log = logger("BillingClient")

const ROUTINE_ERROR_CODES = new Set(["ORGANIZATION_NOT_FOUND"])
const MARKETPLACE_ONBOARDING_PATH = "/aws-marketplace/onboarding"
const MARKETPLACE_ONBOARDING_ROUTINE_ERROR_CODES = new Set<string>(
  Object.values(AWS_MARKETPLACE_ONBOARDING_ERROR_CODES),
)
const SIGNUP_WELCOME_PATHNAME = "/signup-welcome"
const SUBSCRIPTION_PATH = "/billing/subscription"
// SUBSCRIPTION_NOT_FOUND is handled (useSignupCompletionRedirect routes to
// signup-welcome) and is usually an abandoned signup, but for an *active* org
// it means a dropped provisioning — worth a warn so a persistent case is visible
// rather than silently masked by the redirect.
const HANDLED_ERROR_CODES = new Set(["SUBSCRIPTION_NOT_FOUND"])

function currentPathname(): string | undefined {
  if (typeof window === "undefined") {
    return undefined
  }
  return window.location.pathname
}

function isExpectedSignupWelcomeProbe(
  path: string,
  error: BillingApiError,
): boolean {
  return (
    path === SUBSCRIPTION_PATH &&
    error.status === 401 &&
    error.errorCode === "NO_ACTIVE_ORG" &&
    currentPathname() === SIGNUP_WELCOME_PATHNAME
  )
}

function logBillingError(path: string, error: BillingApiError): void {
  const data = { path, status: error.status, errorCode: error.errorCode }
  if (
    error.status < 500 &&
    (ROUTINE_ERROR_CODES.has(error.errorCode) ||
      (path === MARKETPLACE_ONBOARDING_PATH &&
        MARKETPLACE_ONBOARDING_ROUTINE_ERROR_CODES.has(error.errorCode)) ||
      isExpectedSignupWelcomeProbe(path, error))
  ) {
    log.debug("billing request returned routine client error", data)
    return
  }
  if (error.status < 500 && HANDLED_ERROR_CODES.has(error.errorCode)) {
    log.warn("billing request returned a handled client error", data)
    return
  }
  log.error("billing request failed", error, data)
}

type FetchLike = typeof fetch

export interface BillingClient {
  signupInit: (input: SignupInitRequest) => Promise<SignupInitResponse>
  getSubscription: () => Promise<SubscriptionResponse>
  getUsage: () => Promise<UsageResponse>
  createPortalSession: (flow: PortalFlow) => Promise<PortalSessionResponse>
  createCheckoutSession: (
    input: CreateCheckoutSessionRequest,
  ) => Promise<CreateCheckoutSessionResponse>
  onboardMarketplaceOrg: (
    input: MarketplaceOnboardingRequest,
  ) => Promise<MarketplaceOnboardingResponse>
}

interface CreateBillingClientOptions {
  getToken: () => Promise<string | null>
  baseUrl?: string
  fetch?: FetchLike
}

async function readBody(
  status: number,
  response: Response,
  fallbackMessage: string,
): Promise<BillingApiError> {
  let body: BillingErrorBody | undefined
  try {
    body = (await response.json()) as BillingErrorBody
  } catch {
    body = undefined
  }
  return new BillingApiError(status, body, fallbackMessage)
}

export function createBillingClient({
  getToken,
  baseUrl = BILLING_API_BASE_URL,
  fetch: fetchImpl = fetch,
}: CreateBillingClientOptions): BillingClient {
  const normalizedBaseUrl = baseUrl?.replace(/\/+$/, "") ?? baseUrl
  async function authedRequest<T>(
    path: string,
    init: RequestInit,
    fallbackMessage: string,
  ): Promise<T> {
    try {
      return await sendAuthedRequest<T>(path, init, fallbackMessage)
    } catch (error) {
      if (error instanceof BillingApiError) {
        logBillingError(path, error)
      } else if (isClientNetworkError(error)) {
        // The browser couldn't reach the billing API (offline, blocked, CORS) — not a Coval fault.
        log.warn("billing request could not reach the server", { path })
      } else {
        // Genuine unexpected failure (e.g. the response body failed to parse).
        log.error("billing request threw", error, { path })
      }
      throw error
    }
  }

  async function sendAuthedRequest<T>(
    path: string,
    init: RequestInit,
    fallbackMessage: string,
  ): Promise<T> {
    if (!normalizedBaseUrl) {
      throw new BillingApiError(
        500,
        {
          error_code: "CONFIGURATION_ERROR",
          message:
            "Billing API is not configured. Set NEXT_PUBLIC_BILLING_API_BASE_URL.",
        },
        fallbackMessage,
      )
    }
    const token = await getToken()
    if (!token) {
      throw new BillingApiError(
        401,
        { error_code: "UNAUTHENTICATED", message: "Missing Clerk session" },
        fallbackMessage,
      )
    }
    const headers = new Headers(init.headers)
    headers.set("Authorization", `Bearer ${token}`)
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json")
    }
    const response = await fetchImpl(`${normalizedBaseUrl}${path}`, {
      ...init,
      headers,
    })
    if (!response.ok) {
      throw await readBody(response.status, response, fallbackMessage)
    }
    return (await response.json()) as T
  }

  return {
    signupInit: (input) =>
      authedRequest<SignupInitResponse>(
        "/billing/signup-init",
        { method: "POST", body: JSON.stringify(input) },
        "Failed to start signup",
      ),
    getSubscription: () =>
      authedRequest<SubscriptionResponse>(
        "/billing/subscription",
        { method: "GET" },
        "Failed to load subscription",
      ),
    getUsage: () =>
      authedRequest<UsageResponse>(
        "/billing/usage",
        { method: "GET" },
        "Failed to load usage",
      ),
    createPortalSession: (flow) =>
      authedRequest<PortalSessionResponse>(
        "/billing/portal-session",
        { method: "POST", body: JSON.stringify({ flow }) },
        "Failed to open billing portal",
      ),
    createCheckoutSession: (input) =>
      authedRequest<CreateCheckoutSessionResponse>(
        "/billing/create-checkout-session",
        { method: "POST", body: JSON.stringify(input) },
        "Failed to start checkout",
      ),
    onboardMarketplaceOrg: (input) =>
      authedRequest<MarketplaceOnboardingResponse>(
        "/aws-marketplace/onboarding",
        { method: "POST", body: JSON.stringify(input) },
        "Failed to continue AWS Marketplace onboarding",
      ),
  }
}
